import React, { useState, useRef, useEffect } from 'react'
import { useSummariseDiscussionMutation } from '../../store/api/discussionsApi'
import type { SummaryResult } from '../../store/api/discussionsApi'
import s from '../../styles/pages/DiscussionDetailPage.module.css'
import { AI_SUMMARY_ERROR_RESET_MS } from '../../lib/config'

interface AISummaryButtonProps {
  discussionId: string
  /**
   * "inline" — compact button with floating panel (used in the header action row)
   * "widget" — full-width card below the post body (used in sidebar/mobile)
   */
  variant?: 'inline' | 'widget'
}

function SummaryPanel({ summary, variant }: { summary: SummaryResult; variant: 'inline' | 'widget' }) {
  return (
    <div
      className={`${s.summaryPanel}${variant === 'inline' ? ` ${s.summaryPanelInline}` : ''}`}
    >
      <div className={s.summaryWidgetTitle}>{summary.headline}</div>
      <p className={s.summaryWidgetBody}>{summary.summary}</p>
      {summary.keyPoints.length > 0 && (
        <div className={s.summaryPointsList}>
          {summary.keyPoints.map((pt, i) => (
            <div key={i} className={s.summaryPointItem}>
              <span className={s.summaryPointArrow}>→</span>
              <span>{pt}</span>
            </div>
          ))}
        </div>
      )}
      <div className={s.summaryMeta}>
        <span>✨</span> AI-generated summary{variant === 'widget' ? ' · may not be 100% accurate' : ''}
      </div>
    </div>
  )
}

export function AISummaryButton({ discussionId, variant = 'inline' }: AISummaryButtonProps) {
  const [summarise, { isLoading }] = useSummariseDiscussionMutation()
  const [summary, setSummary] = useState<SummaryResult | null>(null)
  const [error, setError] = useState(false)
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  // Close inline panel on outside click
  useEffect(() => {
    if (variant !== 'inline' || !open) return
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open, variant])

  const handleClick = async () => {
    if (summary) { setOpen(o => !o); return }
    try {
      const res = await summarise(discussionId).unwrap()
      setSummary(res.summary)
      setOpen(true)
    } catch {
      setError(true)
      setTimeout(() => setError(false), AI_SUMMARY_ERROR_RESET_MS)
    }
  }

  const isActive = open && !!summary

  if (variant === 'inline') {
    return (
      <div ref={wrapRef} style={{ position: 'relative', overflow: 'visible' }}>
        <button
          onClick={() => void handleClick()}
          disabled={isLoading}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.3rem',
            padding: '0.28rem 0.65rem', borderRadius: '8px',
            border: `1px solid ${isActive ? 'var(--accent-primary)' : 'var(--border)'}`,
            background: isActive ? 'rgba(124,58,237,0.08)' : 'transparent',
            color: isActive ? 'var(--accent-primary)' : 'var(--text-muted)',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            fontSize: '0.75rem', fontWeight: isActive ? 600 : 400,
            transition: 'all 0.15s', opacity: isLoading ? 0.7 : 1, whiteSpace: 'nowrap',
          }}
          onMouseEnter={e => { if (!isLoading && !isActive) { (e.currentTarget as HTMLButtonElement).style.color = 'var(--accent-primary)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--accent-primary)' } }}
          onMouseLeave={e => { if (!isActive) { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)' } }}
        >
          {isLoading
            ? <><div style={{ width: 8, height: 8, border: '1.5px solid var(--border)', borderTopColor: 'var(--accent-primary)', borderRadius: '50%', animation: 'spin 0.7s linear infinite', flexShrink: 0 }} /> Summary</>
            : error ? '⚠️ Failed'
            : <>{isActive ? '▲' : '✨'} Summary</>
          }
        </button>
        {isActive && (
          <>
            <div
              className={s.mobileBackdrop}
              onClick={() => setOpen(false)}
            />
            <SummaryPanel summary={summary!} variant="inline" />
          </>
        )}
      </div>
    )
  }

  // widget variant
  return (
    <div className={s.summaryWidgetWrap}>
      <button
        onClick={() => void handleClick()}
        disabled={isLoading}
        className={s.summaryBtn}
        style={{
          background: isActive ? 'rgba(124,58,237,0.08)' : 'transparent',
          cursor: isLoading ? 'not-allowed' : 'pointer',
          opacity: isLoading ? 0.7 : 1,
        }}
        onMouseEnter={e => { if (!isLoading) (e.currentTarget as HTMLButtonElement).style.background = 'rgba(124,58,237,0.08)' }}
        onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = 'transparent' }}
      >
        {isLoading
          ? <><div className={s.summarySpinner} /> Summarising...</>
          : error ? <>⚠️ Failed — try again</>
          : <>{isActive ? '▲' : '✨'} {isActive ? 'Hide summary' : 'AI Summary'}</>
        }
      </button>
      {isActive && (
        <div>
          <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(-4px) } to { opacity:1; transform:translateY(0) } }`}</style>
          <SummaryPanel summary={summary!} variant="widget" />
        </div>
      )}
    </div>
  )
}
